import React, { PureComponent } from 'react';
import * as Yup from 'yup';
import localeLookup from '../config/locale';
import { connect } from 'react-redux';
import { ACCESS_LEVELS, PERSON_STATES } from '../constants';
import { createRoleService } from '../services/contentService';
import BoxMessage from './BoxMessage';
import AutocompleteSelect from './formElements/AutocompleteSelect';
import Field from './formElements/Field';
import FormWrapper from './formElements/FormWrapper';
import TextField from './formElements/TextField';
import withAccessControl from './HOC/withAccessControl';
import ModalBody from './modal/ModalBody';
import MultiSelect from './MultiSelect';
import { compareLocal, searchString, sortBy } from '../utils/helpers';

const mapStateToProps = (state) => ({
  currentUserId: state.user.employeeId,
  persons: state.persons,
  spaces: state.spaces.spaces,
  spacesEnabled: state.spaces.enabled,
  activeSpaceId: state.spaces.activeSpaceId,
});

const mapDispatchToProps = {};

class CreateRoleForm extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      selectedUnits: props.initialSelectedUnitIds || [],
      isSubmitting: false,
    };
  }

  canEditAfterCreation = (values) => {
    const { currentUserId, hasAccess, spaces } = this.props;
    const hasAccessRoles = hasAccess([
      ACCESS_LEVELS.champadministrator,
      ACCESS_LEVELS.administrator,
      ACCESS_LEVELS.contentAdministrator,
    ]);
    const isAdminInSelectedSpace = spaces[values.spaceId]
      ? spaces[values.spaceId]?.administrators?.includes(currentUserId)
      : false;
    if (hasAccessRoles) return true;
    if (isAdminInSelectedSpace) return true;
    if (values.editors.includes(currentUserId)) return true;
    if (values.owner === currentUserId) return true;
    return false;
  };

  onSubmit = (values) => {
    const { onCreated } = this.props;
    this.setState({ isSubmitting: true });
    createRoleService(values.name, values.owner, values.editors, values.spaceId)
      .then(({ data: id }) => {
        onCreated(id);
      })
      .catch(() => {});
  };

  render() {
    const { isSubmitting } = this.state;
    const {
      persons,
      organisationUnits,
      activeSpaceId,
      renderFooter,
      initialName,
      spacesEnabled,
      hasAccess,
      currentUserId,
      spaces,
      allowSpaceSelection,
      hasCreateRolePermission,
    } = this.props;

    const spaceOptions = sortBy(
      Object.keys(spaces).reduce((acc, id) => {
        const space = spaces[id];
        const canCreateRolesInSpace = hasCreateRolePermission({
          spaceId: space.id,
        });
        if (canCreateRolesInSpace) {
          return [...acc, { label: spaces[id].name, value: id }];
        }

        return acc;
      }, []),
      [(a, b) => compareLocal(a.label, b.label)]
    );

    const ownerOptions = [
      {
        value: '',
        label: localeLookup('translations.No owner'),
        default: true,
        searchString: localeLookup('translations.No owner'),
      },
      ...sortBy(
        Object.keys(persons)
          .filter((id) => persons[id].state === PERSON_STATES.ACTIVE)
          .map((id) => {
            const person = persons[id];
            return {
              label: persons[id].name,
              value: id,
              searchString: `${person.name}${person.employeeNumber}${person.initials}`,
            };
          }),
        [(a, b) => compareLocal(a.label, b.label)]
      ),
    ];
    const editorOptions = [
      ...sortBy(
        Object.keys(persons)
          .filter((id) => persons[id].state === PERSON_STATES.ACTIVE)
          .map((id) => {
            const person = persons[id];
            return {
              label: persons[id].name,
              value: id,
              searchString: `${person.name}${person.employeeNumber}${person.initials}`,
            };
          }),
        [(a, b) => compareLocal(a.label, b.label)]
      ),
    ];
    const validationSchema = Yup.object().shape({
      name: Yup.string().required(
        localeLookup('translations.Name is required')
      ),
      spaceId: Yup.string().required(
        localeLookup('translations.Content space is required')
      ),
    });
    const defaultOwnerId = hasAccess([
      ACCESS_LEVELS.champadministrator,
      ACCESS_LEVELS.administrator,
      ACCESS_LEVELS.contentAdministrator,
    ])
      ? ''
      : currentUserId;

    const filterOption = (candidate, input) => {
      if (input) {
        if (candidate.data.searchString) {
          return searchString(candidate.data.searchString, input);
        }
        return false;
      }
      return true;
    };
    return (
      <FormWrapper
        onSubmit={this.onSubmit}
        validateOnMount
        validationSchema={validationSchema}
        initialValues={{
          name: initialName,
          owner: defaultOwnerId,
          editors: [],
          spaceId: activeSpaceId,
        }}
      >
        {({
          handleSubmit,
          values,
          handleChange,
          touched,
          errors,
          handleBlur,
          isValid,
          setFieldValue,
        }) => (
          <>
            {!this.canEditAfterCreation(values) && (
              <BoxMessage type="warning" spacing="md" icon="warning">
                {localeLookup(
                  'translations.To create this role, you must assign yourself as owner or editor'
                )}
              </BoxMessage>
            )}
            <ModalBody>
              <form
                className="create-role-form"
                onSubmit={handleSubmit}
                autoComplete="off"
              >
                <TextField
                  required
                  autoFocus
                  placeholder={localeLookup('translations.Name')}
                  name="name"
                  id="name"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={touched.name && errors.name}
                  value={values.name}
                  label={localeLookup('translations.Name')}
                />
                <Field label={localeLookup('translations.Owner')}>
                  <AutocompleteSelect
                    defaultValue={values.owner}
                    name="owner"
                    onChange={(selectedOption) => {
                      setFieldValue('owner', selectedOption.value);
                    }}
                    filterOption={filterOption}
                    options={ownerOptions}
                  ></AutocompleteSelect>
                </Field>
                <Field label={localeLookup('translations.Editors')}>
                  <MultiSelect
                    onChange={(values) => {
                      setFieldValue(
                        'editors',
                        values.map((value) => value.value)
                      );
                    }}
                    filterOption={filterOption}
                    name="editors"
                    options={editorOptions}
                    placeholder={localeLookup('translations.Select editors')}
                  />
                </Field>
                {spacesEnabled && (
                  <Field
                    required
                    label={localeLookup('translations.Content space')}
                  >
                    <AutocompleteSelect
                      defaultValue={values.spaceId}
                      name="spaceId"
                      placeholder={localeLookup(
                        'translations.Select content space'
                      )}
                      onChange={(selectedOption) => {
                        setFieldValue('spaceId', selectedOption.value);
                      }}
                      options={spaceOptions}
                      disabled={!allowSpaceSelection}
                    ></AutocompleteSelect>
                  </Field>
                )}
              </form>
            </ModalBody>
            {renderFooter &&
              renderFooter({
                handleSubmit,
                canSubmit:
                  isValid && !isSubmitting && this.canEditAfterCreation(values),
              })}
          </>
        )}
      </FormWrapper>
    );
  }
}

export default connect(mapStateToProps)(withAccessControl(CreateRoleForm));
